import { createContext, useCallback, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useRecorder, type UseRecorderResult } from '@/lib/hooks/use-recorder';

export type RecorderContextValue = {
  recorder: UseRecorderResult;
  activeArrangementId: string | null;
  returnPath: string | null;
  activePracticeSessionId: string | null;
  setActivePracticeSessionId: (sessionId: string | null) => void;
  startFor: (arrangementId: string, returnPath: string) => Promise<void>;
  clearActive: () => void;
};

const RecorderContext = createContext<RecorderContextValue | null>(null);

export function RecorderProvider({ children }: { children: ReactNode }): ReactElement {
  const recorder = useRecorder();
  const [activeArrangementId, setActiveArrangementId] = useState<string | null>(null);
  const [returnPath, setReturnPath] = useState<string | null>(null);
  const [activePracticeSessionId, setActivePracticeSessionId] = useState<string | null>(null);

  const startFor = useCallback(
    async (arrangementId: string, path: string): Promise<void> => {
      setActiveArrangementId(arrangementId);
      setReturnPath(path);
      await recorder.start();
    },
    [recorder],
  );

  const clearActive = useCallback((): void => {
    setActiveArrangementId(null);
    setReturnPath(null);
    setActivePracticeSessionId(null);
  }, []);

  const value = useMemo<RecorderContextValue>(
    () => ({
      recorder,
      activeArrangementId,
      returnPath,
      activePracticeSessionId,
      setActivePracticeSessionId,
      startFor,
      clearActive,
    }),
    [recorder, activeArrangementId, returnPath, activePracticeSessionId, startFor, clearActive],
  );

  return <RecorderContext.Provider value={value}>{children}</RecorderContext.Provider>;
}

export function useRecorderContext(): RecorderContextValue {
  const ctx = useContext(RecorderContext);
  if (!ctx) {
    throw new Error('useRecorderContext must be used within a RecorderProvider');
  }
  return ctx;
}
